/** Pure account-menu view model projected from the backend-resolved account shell. */
import type { TFunction } from 'i18next';
import type {
  AccountShellExperience,
  IconId,
  RouteId,
} from '@finance/contracts';
import type { User } from '../../features/auth/auth.types';
import { resolveTKey } from '../../i18n/resolve-key';

export interface AccountContextOption {
  membershipId: string;
  branchId: string;
  organizationName: string;
  branchName?: string;
  selected: boolean;
  disabled: boolean;
}

export interface AccountMenuAction {
  id: string;
  routeId: RouteId;
  label: string;
  description?: string;
  iconKey?: IconId;
  available: boolean;
  disabledReasonKey?: string;
  remediation?: { action: string; routeId?: RouteId };
  activeRouteIds: readonly RouteId[];
}

export interface AccountMenuViewModel {
  identity: { initials: string; displayName: string; email: string; fallback: boolean };
  personaLabel?: string;
  workspace: AccountShellExperience['workspace'] | null;
  contexts: AccountContextOption[];
  actions: AccountMenuAction[];
  delegated: boolean;
  canExitDelegation: boolean;
  exitDelegationDisabledReasonKey?: string;
  switchPending: boolean;
  logoutPending: boolean;
  switchError: string | null;
}

const USER_FALLBACK_KEY = 'shell:userMenu.userFallback';

export function buildInitials(displayName: string, email: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]}${parts.at(-1)![0]}`.toUpperCase();
  if (parts.length === 1 && parts[0]) return parts[0][0]!.toUpperCase();
  if (email) return email[0]!.toUpperCase();
  return 'U';
}

export function buildAccountMenuViewModel(
  shell: AccountShellExperience | undefined,
  fallbackUser: User | null,
  t: TFunction,
  options: {
    switchPending?: boolean;
    logoutPending?: boolean;
    switchError?: string | null;
  } = {},
): AccountMenuViewModel {
  const fallbackDisplayName = [fallbackUser?.firstName, fallbackUser?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || fallbackUser?.email || t(resolveTKey(USER_FALLBACK_KEY));
  const displayName = shell?.identity.displayName || fallbackDisplayName;
  const email = shell?.identity.email || fallbackUser?.email || '';

  const contexts: AccountContextOption[] = (shell?.contextSwitch.options ?? []).map((context) => ({
    membershipId: context.membershipId,
    branchId: context.branchId,
    organizationName: context.organizationName,
    branchName: context.branchName,
    selected: context.selected,
    disabled: context.selected,
  }));

  const actions: AccountMenuAction[] = (shell?.destinations ?? []).map((item) => ({
    id: item.id,
    routeId: item.routeId,
    label: t(resolveTKey(item.labelKey)),
    description: item.descriptionKey ? t(resolveTKey(item.descriptionKey)) : undefined,
    iconKey: item.iconKey,
    available: item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY',
    disabledReasonKey: item.disabledReasonKey,
    remediation: item.remediation,
    activeRouteIds: item.activeRouteIds,
  }));

  const exitDelegation = shell?.actions.exitDelegation;

  return {
    identity: {
      initials: buildInitials(displayName, email),
      displayName,
      email,
      fallback: !shell,
    },
    personaLabel: shell?.identity.personaLabelKey
      ? t(resolveTKey(shell.identity.personaLabelKey))
      : undefined,
    workspace: shell?.workspace ?? null,
    contexts,
    actions,
    delegated: shell?.workspace?.delegated === true,
    canExitDelegation: exitDelegation?.availability === 'AVAILABLE' || exitDelegation?.availability === 'READ_ONLY',
    exitDelegationDisabledReasonKey: exitDelegation?.disabledReasonKey,
    switchPending: options.switchPending ?? false,
    logoutPending: options.logoutPending ?? false,
    switchError: options.switchError ?? null,
  };
}
